import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AccountsListPage } from './pages/AccountsListPage';
import { AccountDetailPage } from './pages/AccountDetailPage';
import { ContactsListPage } from './pages/ContactsListPage';
import { ContactDetailPage } from './pages/ContactDetailPage';
import { DealsListPage } from './pages/DealsListPage';
import { DealDetailPage } from './pages/DealDetailPage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { ExecutiveBriefingPage } from './pages/ExecutiveBriefingPage';
import { IntegrationsPage } from './pages/IntegrationsPage';
import DealRoomPage from './pages/DealRoomPage';
import { DashboardPage } from './pages/DashboardPage';
import CompanyProfilePage from './pages/CompanyProfilePage';
import UserProfilePage from './pages/UserProfilePage';
import DealRoomSettingsPage from './pages/DealRoomSettingsPage';
import { PortalProposalPage } from './pages/PortalProposalPage';
import { ForecastPage } from './pages/ForecastPage';
import { CoachsCornerPage } from './pages/CoachsCornerPage';
import LoginPageFirebase from './pages/LoginPageFirebase';
import SettingsPage from './pages/SettingsPage';
import WorkflowsPage from './pages/WorkflowsPage';
import WorkflowEditorPage from './pages/WorkflowEditorPage';
import DocumentsPage from './pages/DocumentsPage';
import HelpPage from './pages/HelpPage';
import PrivateRoute from './components/PrivateRoute';
import SidebarLayout from './components/SidebarLayout';
import { GoogleWorkspaceIntegration } from './components/GoogleWorkspaceIntegration';

// Import the new advanced pages we created
import TaskManagementPage from './pages/TaskManagementPage';
import AdvancedAnalyticsPage from './pages/AnalyticsPageMinimal';
import AIInsightsDashboard from './pages/AIInsightsDashboard';
import EmailCalendarIntegrationPage from './pages/EmailCalendarIntegrationPage';
import BusinessPlansPage from './pages/BusinessPlansPage';
import ExecutiveDashboardPage from './pages/ExecutiveDashboardPage';
import ProposalsPage from './pages/ProposalsPage';
import DealRoomTemplatesPage from './pages/DealRoomTemplatesPage';
import DocumentTemplatesPage from './pages/DocumentTemplatesPage';
import GovernmentTemplatesPage from './pages/GovernmentTemplatesPage';
import SBALendingPage from './pages/FundingCirclePage';
import SearchPage from './pages/SearchPage';
import { AuthProvider } from './contexts/AuthContext';
import SSOTokenHandler from './components/SSOTokenHandler';
import { TeamPerformancePage } from './pages/TeamPerformancePage';
import LandingPage from './pages/LandingPage';
import ReferralPage from './pages/ReferralPage';
// import ExecutiveDashboard from './pages/ExecutiveDashboard';

// Create a client with better error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: any) => {
        // Don't retry on 401 errors (authentication issues)
        if (error?.response?.status === 401) {
          return false;
        }
        return failureCount < 3;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Layout for the portal (no CRM navigation)
const PortalLayout = ({ children }: { children: React.ReactNode }) => (
  <>
    {children}
  </>
);

function App() {
  console.log('🎯 App component rendering...');

  // Capture ?ref= query param on any page load and persist for post-login tracking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('ref');
    if (ref) {
      localStorage.setItem('crm_ref', ref);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <SSOTokenHandler>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPageFirebase />} />
              <Route path="/" element={<LandingPage />} />

          {/* Portal routes - these don't require authentication */}
          <Route path="/portal/proposals/:shareToken" element={
            <PortalLayout>
              <PortalProposalPage />
            </PortalLayout>
          } />

          {/* Profile pages - accessible without authentication for demo purposes */}
          <Route path="/company-profile" element={<CompanyProfilePage />} />
          <Route path="/user-profile" element={<UserProfilePage />} />

          {/* Protected routes */}
          <Route element={<PrivateRoute />}>
            <Route path="/home" element={
              <SidebarLayout>
                <DashboardPage />
              </SidebarLayout>
            } />
            <Route path="/dashboard" element={
              <SidebarLayout>
                <DashboardPage />
              </SidebarLayout>
            } />
            <Route path="/accounts" element={
              <SidebarLayout>
                <AccountsListPage />
              </SidebarLayout>
            } />
            <Route path="/accounts/:accountId" element={
              <SidebarLayout>
                <AccountDetailPage />
              </SidebarLayout>
            } />
            <Route path="/contacts" element={
              <SidebarLayout>
                <ContactsListPage />
              </SidebarLayout>
            } />
            <Route path="/contacts/:contactId" element={
              <SidebarLayout>
                <ContactDetailPage />
              </SidebarLayout>
            } />
            <Route path="/deals" element={
              <SidebarLayout>
                <DealsListPage />
              </SidebarLayout>
            } />
            <Route path="/deals/:dealId" element={
              <SidebarLayout>
                <DealDetailPage />
              </SidebarLayout>
            } />
            <Route path="/deals/:dealId/room" element={
              <SidebarLayout>
                <DealRoomPage />
              </SidebarLayout>
            } />
            <Route path="/tasks" element={
              <SidebarLayout>
                <TaskManagementPage />
              </SidebarLayout>
            } />
            <Route path="/documents" element={
              <SidebarLayout>
                <DocumentsPage />
              </SidebarLayout>
            } />
            <Route path="/help" element={
              <SidebarLayout>
                <HelpPage />
              </SidebarLayout>
            } />
            <Route path="/search" element={
              <SidebarLayout>
                <SearchPage />
              </SidebarLayout>
            } />
            <Route path="/analytics" element={
              <SidebarLayout>
                <AdvancedAnalyticsPage />
              </SidebarLayout>
            } />
            <Route path="/ai-insights" element={
              <SidebarLayout>
                <AIInsightsDashboard />
              </SidebarLayout>
            } />
            <Route path="/email-calendar" element={
              <SidebarLayout>
                <EmailCalendarIntegrationPage />
              </SidebarLayout>
            } />
            <Route path="/google-workspace" element={
              <SidebarLayout>
                <GoogleWorkspaceIntegration />
              </SidebarLayout>
            } />
            <Route path="/business-plans" element={
              <SidebarLayout>
                <BusinessPlansPage />
              </SidebarLayout>
            } />
            <Route path="/executive" element={
              <SidebarLayout>
                <ExecutiveDashboardPage />
              </SidebarLayout>
            } />
            <Route path="/proposals" element={
              <SidebarLayout>
                <ProposalsPage />
              </SidebarLayout>
            } />
            <Route path="/templates" element={
              <SidebarLayout>
                <DealRoomTemplatesPage />
              </SidebarLayout>
            } />
            <Route path="/document-templates" element={
              <SidebarLayout>
                <DocumentTemplatesPage />
              </SidebarLayout>
            } />
            {/* <Route path="/executive" element={
              <SidebarLayout>
                <ExecutiveDashboard />
              </SidebarLayout>
            } /> */}
            <Route path="/settings" element={
              <SidebarLayout>
                <SettingsPage />
              </SidebarLayout>
            } />
            <Route path="/integrations" element={
              <SidebarLayout>
                <IntegrationsPage />
              </SidebarLayout>
            } />

            <Route path="/deal-room-settings" element={
              <SidebarLayout>
                <DealRoomSettingsPage />
              </SidebarLayout>
            } />

            <Route path="/government-templates" element={
              <SidebarLayout>
                <GovernmentTemplatesPage />
              </SidebarLayout>
            } />

            <Route path="/funding-circle" element={
              <SidebarLayout>
                <SBALendingPage />
              </SidebarLayout>
            } />

            <Route path="/referrals" element={
              <SidebarLayout>
                <ReferralPage />
              </SidebarLayout>
            } />

            {/* Manager/Executive routes with role-based access control */}
            <Route element={<PrivateRoute requiredRoles={['Manager', 'Executive', 'Admin']} />}>
              <Route path="/forecast" element={
                <SidebarLayout>
                  <ForecastPage />
                </SidebarLayout>
              } />
              <Route path="/coachs-corner" element={
                <SidebarLayout>
                  <CoachsCornerPage />
                </SidebarLayout>
              } />
              <Route path="/performance" element={
                <SidebarLayout>
                  <TeamPerformancePage />
                </SidebarLayout>
              } />
            </Route>

            {/* Executive-only routes */}
            <Route element={<PrivateRoute requiredRoles={['Executive', 'Admin']} />}>
              <Route path="/executive-briefing" element={
                <SidebarLayout>
                  <ExecutiveBriefingPage />
                </SidebarLayout>
              } />
            </Route>

            {/* Admin-only routes */}
            <Route element={<PrivateRoute requiredRoles={['Admin']} />}>
              <Route path="/settings/workflows" element={
                <SidebarLayout>
                  <WorkflowsPage />
                </SidebarLayout>
              } />
              <Route path="/settings/workflows/create" element={
                <SidebarLayout>
                  <WorkflowEditorPage />
                </SidebarLayout>
              } />
              <Route path="/settings/workflows/edit/:id" element={
                <SidebarLayout>
                  <WorkflowEditorPage />
                </SidebarLayout>
              } />
            </Route>
          </Route>
        </Routes>
          </SSOTokenHandler>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;